import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, InputGroup, ButtonGroup } from 'react-bootstrap';
import { FaSearch, FaFilter, FaEye, FaCheck, FaTimes } from 'react-icons/fa';
import { DealFilters as FilterType } from '../../services/dealService';

interface DealFiltersProps {
  onFilterChange: (filters: Partial<FilterType>) => void;
  currentFilters: FilterType;
}

const DealFilters: React.FC<DealFiltersProps> = ({ onFilterChange, currentFilters }) => {
  const [localFilters, setLocalFilters] = useState({
    search: '',
    grade: '',
    city: '',
    status: currentFilters.status || '',
    max_price: '',
    min_roi: '',
    zip: '',
    fixer_only: false
  });

  // Cities list - could be fetched from API
  const cities = [
    'All Cities',
    'Phoenix',
    'Scottsdale',
    'Mesa',
    'Chandler',
    'Gilbert',
    'Tempe',
    'Glendale',
    'Peoria',
    'Surprise',
    'Avondale'
  ];

  const grades = ['All', 'A+', 'A', 'B', 'C', 'D', 'F'];
  const statuses = ['All', 'new', 'reviewing', 'pursuing', 'offered', 'won', 'passed', 'expired'];
  const maxPrices = [
    { label: 'Any Price', value: '' },
    { label: 'Under $200K', value: '200000' },
    { label: 'Under $300K', value: '300000' },
    { label: 'Under $400K', value: '400000' },
    { label: 'Under $500K', value: '500000' },
    { label: 'Under $750K', value: '750000' }
  ];
  const minROIs = [
    { label: 'Any ROI', value: '' },
    { label: '10%+', value: '10' },
    { label: '15%+', value: '15' },
    { label: '20%+', value: '20' },
    { label: '25%+', value: '25' },
    { label: '30%+', value: '30' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setLocalFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleApplyFilters = () => {
    const filters: Partial<FilterType> = {};
    
    // Add search filter for address
    if (localFilters.search && localFilters.search.trim() !== '') {
      filters.search = localFilters.search.trim();
    }
    
    // Only add non-empty filters
    if (localFilters.grade && localFilters.grade !== 'All') {
      filters.grade = localFilters.grade;
    }
    if (localFilters.city && localFilters.city !== 'All Cities') {
      filters.city = localFilters.city;
    }
    if (localFilters.status && localFilters.status !== 'All') {
      filters.status = localFilters.status;
    }
    if (localFilters.max_price) {
      filters.max_price = Number(localFilters.max_price);
    }
    if (localFilters.min_roi) {
      filters.min_roi = Number(localFilters.min_roi);
    }
    if (localFilters.zip) {
      filters.zip = localFilters.zip;
    }
    if (localFilters.fixer_only) {
      filters.fixer_only = true;
    }
    
    onFilterChange(filters);
  };

  const handleClearFilters = () => {
    setLocalFilters({
      search: '',
      grade: '',
      city: '',
      status: '',
      max_price: '',
      min_roi: '',
      zip: '',
      fixer_only: false
    });
    onFilterChange({
      search: undefined,
      grade: undefined,
      city: undefined,
      status: undefined,
      max_price: undefined,
      min_roi: undefined,
      zip: undefined,
      fixer_only: undefined
    });
  };

  const handleQuickStatusFilter = (status: string) => {
    setLocalFilters(prev => ({ ...prev, status }));
    onFilterChange({ status: status === 'All' ? undefined : status });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <FaFilter className="me-2" />
          Filters
        </h5>
        
        {/* Quick Status Filters */}
        <ButtonGroup size="sm">
          <Button
            variant={currentFilters.status === undefined ? "primary" : "outline-primary"}
            onClick={() => handleQuickStatusFilter('All')}
            size="sm"
          >
            <FaEye className="me-1" /> All
          </Button>
          <Button
            variant={currentFilters.status === 'new' ? "primary" : "outline-primary"}
            onClick={() => handleQuickStatusFilter('new')}
            size="sm"
          >
            🆕 New
          </Button>
          <Button
            variant={currentFilters.status === 'reviewing' ? "info" : "outline-info"}
            onClick={() => handleQuickStatusFilter('reviewing')}
            size="sm"
          >
            🔍 Review
          </Button>
          <Button
            variant={currentFilters.status === 'pursuing' ? "warning" : "outline-warning"}
            onClick={() => handleQuickStatusFilter('pursuing')}
            size="sm"
          >
            🎯 Pursue
          </Button>
          <Button
            variant={currentFilters.status === 'offered' ? "success" : "outline-success"}
            onClick={() => handleQuickStatusFilter('offered')}
            size="sm"
          >
            💰 Offered
          </Button>
          <Button
            variant={currentFilters.status === 'won' ? "success" : "outline-success"}
            onClick={() => handleQuickStatusFilter('won')}
            size="sm"
          >
            <FaCheck /> Won
          </Button>
          <Button
            variant={currentFilters.status === 'passed' ? "secondary" : "outline-secondary"}
            onClick={() => handleQuickStatusFilter('passed')}
            size="sm"
          >
            <FaTimes /> Pass
          </Button>
        </ButtonGroup>
      </div>
      
      <Row className="g-3 mb-3">
        {/* Search */}
        <Col xs={12} md={6} lg={3}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search address..."
              name="search"
              value={localFilters.search}
              onChange={handleInputChange}
            />
          </InputGroup>
        </Col>
        
        {/* Grade */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            name="grade"
            value={localFilters.grade}
            onChange={handleInputChange}
          >
            <option value="">Grade</option>
            {grades.map(grade => (
              <option key={grade} value={grade === 'All' ? '' : grade}>
                {grade}
              </option>
            ))}
          </Form.Select>
        </Col>
        
        {/* City */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            name="city"
            value={localFilters.city}
            onChange={handleInputChange}
          >
            <option value="">City</option>
            {cities.map(city => (
              <option key={city} value={city === 'All Cities' ? '' : city}>
                {city}
              </option>
            ))}
          </Form.Select>
        </Col>
        
        {/* Status */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            name="status"
            value={localFilters.status}
            onChange={handleInputChange}
          >
            <option value="">Status</option>
            {statuses.map(status => (
              <option key={status} value={status === 'All' ? '' : status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </Form.Select>
        </Col>
        
        {/* Max Price */}
        <Col xs={6} md={3} lg={3}>
          <Form.Select
            name="max_price"
            value={localFilters.max_price}
            onChange={handleInputChange}
          >
            {maxPrices.map(price => (
              <option key={price.value} value={price.value}>
                {price.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
      
      <Row className="g-3 align-items-end">
        {/* Min ROI */}
        <Col xs={6} md={3} lg={2}>
          <Form.Select
            name="min_roi"
            value={localFilters.min_roi}
            onChange={handleInputChange}
          >
            {minROIs.map(roi => (
              <option key={roi.value} value={roi.value}>
                {roi.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        
        {/* Zip */}
        <Col xs={6} md={3} lg={2}>
          <Form.Control
            type="text"
            placeholder="Zip code..."
            name="zip"
            value={localFilters.zip}
            onChange={handleInputChange}
          />
        </Col>
        
        {/* Fixer Only */}
        <Col xs={6} md={3} lg={2}>
          <Form.Check
            type="checkbox"
            id="fixer_only"
            name="fixer_only"
            label="Fixer Only"
            checked={localFilters.fixer_only}
            onChange={handleInputChange}
          />
        </Col>
        
        {/* Buttons */}
        <Col xs={12} md={6} lg={3} className="ms-auto">
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={handleClearFilters}
              className="flex-fill"
            >
              Clear
            </Button>
            <Button
              variant="primary"
              onClick={handleApplyFilters}
              className="flex-fill"
            >
              Apply Filters
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default DealFilters;